import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { Animated } from "react-native-web";
import { tokens } from "@opendoor/tokens-studio/dist/bricks-next/tokens/Components";
import { isWeb, styled, View } from "@tamagui/core";
import { useSwitch } from "@tamagui/switch-headless";
import { Text } from "../Text";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
const { toggle: toggleTokens } = tokens, Frame = styled(View, {
  // Use <button> tag for a11y so space/enter keys toggle the switch.
  // See: https://www.w3.org/WAI/ARIA/apg/patterns/switch/
  tag: "button",
  // @ts-expect-error - need type="button" to prevent form submission on web
  type: "button",
  padding: 0,
  flexDirection: "row",
  alignSelf: "flex-start",
  backgroundColor: toggleTokens.color.background.element.toggle.default,
  cursor: "pointer",
  borderStyle: "solid",
  borderWidth: toggleTokens.borderWidth.element.state.default,
  borderColor: toggleTokens.color.border.element.toggle.state.default,
  outlineStyle: "none",
  borderRadius: toggleTokens.cornerRadius.element.toggle.large,
  animation: "accelerateDecelerateFastest",
  animateOnly: isWeb ? ["background-color", "border-color"] : ["backgroundColor", "borderColor"],
  focusVisibleStyle: {
    borderColor: toggleTokens.color.border.element.toggle.state.focused
  },
  variants: {
    frameSize: (size) => ({
      height: toggleTokens.size.element.onOff[size]
    }),
    hasError: {
      true: {
        borderColor: toggleTokens.color.border.element.toggle.state.error,
        "aria-invalid": !0
      }
    },
    disabled: {
      true: {
        cursor: "not-allowed",
        pointerEvents: "none"
      }
    },
    embedded: {
      true: {
        maxWidth: 80
      },
      false: {
        minWidth: 320
      }
    }
  }
}), Content = styled(View, {
  alignItems: "center",
  justifyContent: "center",
  variants: {
    frameSize: (size) => ({
      width: 140,
      height: toggleTokens.size.element.onOff[size],
      borderRadius: toggleTokens.cornerRadius.element.toggle.large
    }),
    embedded: {
      true: {
        maxWidth: 40
      },
      false: {
        minWidth: 140
      }
    }
  }
}), Thumb = styled(View, {
  backgroundColor: toggleTokens.color.background.element.toggle.on,
  variants: {
    frameSize: (size) => ({
      height: toggleTokens.size.element.onOff[size],
      borderRadius: toggleTokens.cornerRadius.element.toggle.large
    }),
    embedded: {
      true: {
        maxWidth: 80
      },
      false: {
        minWidth: 140
      }
    },
    hasError: {
      true: {
        backgroundColor: toggleTokens.color.background.element.toggle.state.errorOn
      }
    },
    disabled: {
      true: {
        backgroundColor: toggleTokens.color.background.element.toggle.state.inactiveOn
      }
    }
  }
}), Toggle = forwardRef(function({ leftIcon, rightIcon, size, hasError, leftLabel, rightLabel, isEmbeddedInTextInput, ...props }, ref) {
  const [checked, setChecked] = useState(props.defaultChecked || !1), { switchProps, switchRef, bubbleInput } = useSwitch(props, [checked, setChecked], ref), { onCheckedChange, ...restSwitchProps } = switchProps;
  useEffect(() => {
    onCheckedChange?.(checked);
  }, [checked]);
  const animation = useRef(new Animated.Value(checked ? 1 : 0));
  useEffect(() => {
    Animated.timing(animation.current, {
      toValue: checked ? 1 : 0,
      duration: 80,
      useNativeDriver: !0
    }).start();
  }, [checked]);
  const getIconColor = useCallback(
    (isSelected) => props.disabled ? isSelected ? toggleTokens.color.content.element.toggle.state.inactiveOn : toggleTokens.color.content.element.toggle.state.inactiveOff : hasError && !isSelected ? toggleTokens.color.content.element.toggle.state.error : isSelected ? toggleTokens.color.content.element.toggle.on : toggleTokens.color.content.element.toggle.off,
    [props.disabled, hasError]
  ), [leftWidth, setLeftWidth] = useState(0), [rightWidth, setRightWidth] = useState(0);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      Frame,
      {
        ref: switchRef,
        ...restSwitchProps,
        disabled: !!switchProps.disabled,
        frameSize: size,
        hasError,
        embedded: isEmbeddedInTextInput,
        children: [
          /* @__PURE__ */ jsx(
            Animated.View,
            {
              style: [
                {
                  position: "absolute",
                  width: checked ? rightWidth : leftWidth,
                  transform: [
                    {
                      translateX: animation.current.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, leftWidth]
                      })
                    }
                  ]
                }
              ],
              children: /* @__PURE__ */ jsx(
                Thumb,
                {
                  frameSize: size,
                  hasError,
                  disabled: props.disabled,
                  embedded: isEmbeddedInTextInput
                }
              )
            }
          ),
          /* @__PURE__ */ jsx(
            Content,
            {
              frameSize: size,
              embedded: isEmbeddedInTextInput,
              onLayout: (e) => setLeftWidth(e.nativeEvent.layout.width),
              paddingHorizontal: leftLabel ? "$9x" : "$4x",
              children: rightLabel ? /* @__PURE__ */ jsx(
                Text,
                {
                  tag: "span",
                  typography: size === "small" ? toggleTokens.typography.element.smallSize : toggleTokens.typography.element.mediumSize,
                  color: getIconColor(!checked),
                  children: leftLabel
                }
              ) : leftIcon?.Component && /* @__PURE__ */ jsx(leftIcon.Component, { "aria-label": leftIcon.label, color: getIconColor(!checked) })
            }
          ),
          /* @__PURE__ */ jsx(
            Content,
            {
              frameSize: size,
              embedded: isEmbeddedInTextInput,
              onLayout: (e) => setRightWidth(e.nativeEvent.layout.width),
              paddingHorizontal: rightLabel ? "$9x" : "$4x",
              children: rightLabel ? /* @__PURE__ */ jsx(
                Text,
                {
                  tag: "span",
                  typography: size === "small" ? toggleTokens.typography.element.smallSize : toggleTokens.typography.element.mediumSize,
                  color: getIconColor(checked),
                  children: rightLabel
                }
              ) : rightIcon?.Component && /* @__PURE__ */ jsx(rightIcon.Component, { "aria-label": rightIcon.label, color: getIconColor(checked) })
            }
          )
        ]
      }
    ),
    bubbleInput
  ] });
});
export {
  Toggle
};
